import { YesNoPreferNotToSayType } from "~/common/utils";
import { DirectInquiryType, IndirectInquiryType } from "./patientIntakePreview";
import {
  ResponseQuestionTypes,
  SummaryFeedback,
  SummaryType,
} from "./patientPreviewTypes";
import {
  AudioResponse,
  IntakeAnswer,
  IntakeQuestionType,
} from "./rawResponsesTypes";

export const FUNCTIONAL_DOMAIN_KEY = {
  health: "health",
  relationships: "relationships",
  social_support: "social_support",
  family: "family",
  hope: "hope",
  money: "money",
  enjoyment: "enjoyment",
  work: "work",
  mood: "mood",
  post_partum: "post_partum",
} as const;

export const FUNCTIONAl_DOMAINS_KEY_OPTIONS = Object.values(
  FUNCTIONAL_DOMAIN_KEY,
);

export type FunctionalDomainKeys =
  (typeof FUNCTIONAL_DOMAIN_KEY)[keyof typeof FUNCTIONAL_DOMAIN_KEY];

export interface SymptomProgression {
  symptom: string;
  progression: string;
}

interface SentenceCompletions {
  classification: string;
  sentences: string[];
}

export interface FunctionalDomain {
  direct_answers?: string;
  sentence_completions: SentenceCompletions;
  classification: string;
  narrative?: string[];
}

export interface FunctionalDomainsV1 {
  health: FunctionalDomain;
  relationships: FunctionalDomain;
  social_support: FunctionalDomain;
  family: FunctionalDomain;
  hope: FunctionalDomain;
  money: FunctionalDomain;
  enjoyment: FunctionalDomain;
  work: FunctionalDomain;
  mood: FunctionalDomain;
  post_partum?: FunctionalDomain;
}

export interface IndirectInquiry {
  classification: IndirectInquiryType;
  sentences: string[];
}

export interface Answer {
  value: string;
}

export interface DirectInquiryQuestions {
  question_code: IntakeQuestionType;
  type: IntakeAnswer;
  answer: Answer;
}

export interface DirectInquiry {
  classification: DirectInquiryType;
  questions?: DirectInquiryQuestions[];
}

export interface FunctionalDomainsV2 {
  section: FunctionalDomainKeys;
  direct_inquiry: DirectInquiry;
  indirect_inquiry: IndirectInquiry;
  may_indicate: string;
  narrative?: string;
}

export interface SymptomsProgressionOverTime {
  symptom: string;
  pattern: string;
}

export interface HealthHistory {
  history_of_present_illness: string;
  psychiatric_history: string;
  medical_history: string;
  symptoms_progression_over_time: SymptomsProgressionOverTime[];
}

export interface HealthHistoryV3 extends HealthHistory {
  substance_history: string;
  summary: string;
}

export interface PersonalLifeHistory {
  substance_history: string;
  past_personal_history: string;
  educational_history: string;
  family_history: string;
}

export interface PersonalLifeHistoryV3 extends Omit<
  PersonalLifeHistory,
  "substance_history"
> {
  summary: string;
}

export interface TreatmentRecommendations {
  cognitive_behavioral_therapy: string;
  insight_oriented_therapy: string;
}

export interface TreatmentRecommendationsV3 extends TreatmentRecommendations {
  prescriber_considerations: string;
}

export interface IntakeSummaryDataV1 {
  id: string;
  long_summary: string;
  short_summary: string;
  functional_domains: FunctionalDomainsV1;
  symptom_progression: SymptomProgression[];
  treatment_recommendations: string;
}

export interface IntakeSummaryDataV2 {
  id: string;
  case_summary: string;
  health_history: HealthHistory;
  personal_life_history: PersonalLifeHistory;
  functional_domains: FunctionalDomainsV2[];
  treatment_recommendations: TreatmentRecommendations;
  allergies?: string;
}

export interface AIConversationHistory {
  account: YesNoPreferNotToSayType;
  evaluation: number;
  modifications: string;
  prompt: string;
  response: string;
  extra_thoughts: AudioResponse[];
}

export interface FillmData {
  intro_sentence: string;
  conflicting: Record<"bullets", string[]>;
  congruent: Record<"bullets", string[]>;
  novel: Record<"bullets", string[]>;
}

export interface IntakeSummaryDataV3 {
  id: string;
  case_summary: string;
  health_history: HealthHistoryV3;
  personal_life_history: PersonalLifeHistoryV3;
  functional_domains: FunctionalDomainsV2[];
  treatment_recommendations: TreatmentRecommendationsV3;
  allergies?: string;
  ai_informant_conversation_history?: AIConversationHistory;
  fillm?: FillmData;
}

export type IntakeSummaryDataV4 = IntakeSummaryDataV3;

export interface PatientIntakeConsultationSummaryV1 {
  id: string;
  consultation_id: string;
  feedbacks: SummaryFeedback[];
  type: ResponseQuestionTypes;
  data: IntakeSummaryDataV1;
  version: 1;
}

export interface PatientIntakeConsultationSummaryV2 {
  id: string;
  consultation_id: string;
  feedbacks: SummaryFeedback[];
  type: ResponseQuestionTypes;
  data: IntakeSummaryDataV2;
  version: 2;
}

export interface PatientIntakeConsultationSummaryV3 {
  id: string;
  consultation_id: string;
  feedbacks: SummaryFeedback[];
  type: ResponseQuestionTypes;
  data: IntakeSummaryDataV3;
  version: 3;
}

export interface PatientIntakeConsultationSummaryV4 extends Omit<
  PatientIntakeConsultationSummaryV3,
  "version"
> {
  version: 4;
}

export type PatientIntakeConsultationSummary =
  | PatientIntakeConsultationSummaryV1
  | PatientIntakeConsultationSummaryV2
  | PatientIntakeConsultationSummaryV3
  | PatientIntakeConsultationSummaryV4;

export interface RateSummaryProps {
  summaryId: string;
  consultationId?: string;
  summaryType: SummaryType;
  isLike: boolean | null;
}

export interface UseGetPatientInteractionsActionsProps {
  patientId: string;
  screeningSummaryId: string;
  intakeSummaryId?: string;
}

export interface HandleRateSummaryProps {
  summaryType: SummaryType;
  isLike: boolean | null;
}
